/**
 * Clean Status enum for project clean status
 * Maps to CleanStatus lookup table in the database
 */
export enum CleanStatus {
  NotStarted = 1,
  InProgress = 2,
  Successful = 3,
  Failed = 4,
  Incomplete = 99 // Virtual status - not from database, used for UI only
}

/**
 * Helper functions for CleanStatus enum
 */
export class CleanStatusHelper {
  /**
   * Get clean status label based on cleanStatusId
   * @param cleanStatusId The clean status ID from the API
   * @returns A human-readable status label
   */
  static getLabel(cleanStatusId: number): string {
    switch (cleanStatusId) {
      case CleanStatus.NotStarted:
        return 'Not Started';
      case CleanStatus.InProgress:
        return 'In Progress';
      case CleanStatus.Successful:
        return 'Successful';
      case CleanStatus.Failed:
        return 'Failed';
      case CleanStatus.Incomplete:
        return 'Incomplete Setup';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get status CSS class based on cleanStatusId
   * @param cleanStatusId The clean status ID from the API
   * @returns A CSS class for styling
   */
  static getStatusClass(cleanStatusId: number): string {
    switch (cleanStatusId) {
      case CleanStatus.NotStarted:
        return 'status-paused';
      case CleanStatus.InProgress:
        return 'status-in-progress';
      case CleanStatus.Successful:
        return 'status-completed';
      case CleanStatus.Failed:
        return 'status-failed';
      case CleanStatus.Incomplete:
        return 'status-incomplete';
      default:
        return 'status-paused';
    }
  }

  /**
   * Get status icon based on cleanStatusId
   * @param cleanStatusId The clean status ID from the API
   * @returns A Material icon name or custom SVG icon name
   */
  static getStatusIcon(cleanStatusId: number): string {
    switch (cleanStatusId) {
      case CleanStatus.NotStarted:
        return 'play_circle'; // Iconly Play
      case CleanStatus.InProgress:
        return 'hourglass_empty'; // Iconly Hourglass-slim
      case CleanStatus.Successful:
        return 'check_circle_black'; // Iconly Check-circle (black variant)
      case CleanStatus.Failed:
        return 'error'; // Iconly Error-Triangle
      case CleanStatus.Incomplete:
        return 'warning'; // Iconly Error-Triangle
      default:
        return 'help'; // Iconly info-circle
    }
  }

  /**
   * Check if the icon is a custom SVG icon
   * All icons are now custom Iconly SVG icons registered in app.component.ts
   */
  static isCustomIcon(iconName: string): boolean {
    const customIcons = ['check_circle', 'check_circle_black', 'pause_circle', 'play_arrow', 'error', 'warning', 'help'];
    return customIcons.includes(iconName);
  }

  /**
   * Check if a project is incomplete (missing data source, goals, or outputs)
   * @param project The project to check
   * @returns True if the project is missing required setup
   */
  static isProjectIncomplete(project: { dataSourceId: string | null, goalCount: number, outputCount?: number }): boolean {
    return !project.dataSourceId || project.goalCount < 1 || (project.outputCount ?? 0) < 1;
  }
}
